import React, {
  useState, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';

// Utils
import frLocale from 'date-fns/locale/fr';
import { useTranslation } from 'react-i18next';
import {
  concat, isEmpty, isNil, sortBy,
} from 'lodash';
import {
  addDays,
  startOfWeek,
  format,
  endOfWeek,
  addWeeks,
  isEqual,
  subWeeks,
  isAfter,
  isBefore,
  addYears,
  parse,
  differenceInWeeks,
} from 'date-fns';
import { useMutation, useQuery } from 'react-query';
import { useAppContext } from '../../../../store/AppContext';
import cn from '../../../../utils/tools/cn';
import dayIdTool from '../../../../utils/tools/dayId';
import useModal from '../../../../utils/hooks/useModal';
import seasonTypes from '../../../../constants/seasons';
import displayPrice from '../../../../utils/tools/price';
import useNotification from '../../../../utils/hooks/useNotification';

// Icons
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info.svg';

// Services
import useInventoryService from '../../../../services/inventoryService';
import useSeasonService from '../../../../services/seasonService';

// Components
import InventoryTable from '../../../tables/InventoryTable/InventoryTable';
import EmptyList from '../../../organisms/EmptyList/EmptyList';
import SelectField from '../../../atoms/SelectField/SelectField';
import BatchEdition from '../BatchEdition/BatchEdition';
import TableInputNumber from '../../../atoms/TableInputNumber/TableInputNumber';

import styles from './Yields.module.scss';

const inventoryType = {
  camps: 'CAMP',
  extras: 'EXTRA',
  accommodations: 'ACCOMMODATION',
};

function Yields({ products, type }) {
  const { t } = useTranslation();
  const [{ establishment, notifications }] = useAppContext();
  const { displayModal } = useModal();
  const { displayNotification } = useNotification();

  const { getInventories, putInventoryYield } = useInventoryService();
  const { getSeasons } = useSeasonService();

  const [inventories, setInventories] = useState({});
  const [seasons, setSeasons] = useState([]);

  useQuery('getSeasons', getSeasons, {
    onSuccess: (res) => setSeasons(sortBy(res?.data?.seasons || [], 'startDate')),
  });

  const establishmentCreationDate = useMemo(() => (
    establishment?.creationDate ? parse(establishment.creationDate, 'yyyy-MM-dd', new Date()) : null
  ), [establishment]);

  const getStartOfWeek = (date) => startOfWeek(date, { weekStartsOn: 1 });
  const getEndOfWeek = (date) => endOfWeek(date, { weekStartsOn: 1 });

  const minWeekLimit = useMemo(() => (
    getStartOfWeek(establishmentCreationDate || new Date())
  ), [establishmentCreationDate]);
  const maxWeekLimit = useMemo(() => getStartOfWeek(subWeeks(addYears(new Date(), 1), 1)), []);

  const defaultWeekMonday = useMemo(() => (getStartOfWeek(new Date())), []);
  const [activeWeekMonday, setActiveWeekMonday] = useState(getStartOfWeek(new Date()));
  const activeWeekSunday = useMemo(
    () => (activeWeekMonday ? getEndOfWeek(activeWeekMonday) : null),
    [activeWeekMonday],
  );

  const getFormatStart = (date) => format(getStartOfWeek(date), 'dd/MM/yyyy');
  const getFormatEnd = (date) => format(getEndOfWeek(date), 'dd/MM/yyyy');
  const getWeekLabel = (date) => (
    `${t('logistic.week')} ${format(date, 'I')} (${getFormatStart(date)} - ${getFormatEnd(date)})`
  );

  const weeksOptions = useMemo(() => (
    concat(
      Array.from(Array(
        isBefore(minWeekLimit, new Date()) && differenceInWeeks(getStartOfWeek(new Date()), minWeekLimit) > 0
          ? (differenceInWeeks(getStartOfWeek(new Date()), minWeekLimit))
          : 0,
      ).keys()).map(
        (_, index) => ({
          label: getWeekLabel(addWeeks(minWeekLimit, index)),
          value: getStartOfWeek(addWeeks(minWeekLimit, index)),
        }),
      ),
      Array.from(Array(52).keys()).map(
        (_, index) => ({
          label: getWeekLabel(addWeeks(new Date(), index)),
          value: getStartOfWeek(addWeeks(new Date(), index)),
        }),
      ),
    )
  ), [minWeekLimit]);

  const { isFetching } = useQuery(
    ['getInventories', activeWeekSunday],
    () => getInventories({
      type: inventoryType[type],
      start: dayIdTool.getDayIdFromDate(activeWeekMonday || defaultWeekMonday),
      end: dayIdTool.getDayIdFromDate(activeWeekSunday || getEndOfWeek(defaultWeekMonday)),
    }),
    {
      enabled: !isNil(inventoryType[type]),
      onSuccess: (res) => {
        const inventoriesRaw = res?.data?.inventories || [];
        const inventoriesByProduct = {};
        products.forEach(({ id }) => {
          const inv = inventoriesRaw?.filter((inventory) => inventory.productId === id);
          const invByDay = {};
          inv?.forEach(({
            date, id: inventoryId, price, yielding, quantity, soldQuantity,
          }) => {
            invByDay[date] = {
              id: inventoryId,
              price,
              yielding: +yielding,
              quantity,
              soldQuantity,
            };
          });
          inventoriesByProduct[id] = invByDay;
        });
        setInventories(inventoriesByProduct);
      },
    },
  );

  const updateYieldInInventory = (productId, dayId, yielding) => {
    if (inventories?.[productId]?.[dayId]?.id) {
      setInventories((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [dayId]: {
            ...prev[productId][dayId],
            yielding: +yielding,
          },
        },
      }));
    }
  };

  const {
    isLoading: putYieldIsLoading,
    mutateAsync: putYieldMutateAsync,
  } = useMutation(
    ({ inventoryId, yielding }) => putInventoryYield({ inventoryId, yielding }),
    {
      onMutate: (updated) => {
        if (updated?.productId && updated?.dayId) {
          updateYieldInInventory(updated.productId, updated.dayId, updated.yielding);
        }
      },
      onError: (_, updated) => {
        if (!notifications?.length) {
          displayNotification({
            message: t('yields.error'),
            color: 'error',
          });
        }
        updateYieldInInventory(updated.productId, updated.dayId, updated.prevValue);
      },
    },
  );

  const handleChangeYield = async (inventoryId, yielding, productId, dayId, prevValue) => {
    if (!inventoryId || isNil(yielding) || !productId) return;

    await putYieldMutateAsync({
      inventoryId,
      yielding,
      productId,
      dayId,
      prevValue,
    });
  };

  const getSeasonForDay = useCallback(({ date, id }) => {
    const defaultSeason = seasonTypes.MEDIUM;
    if (!date && !id) return defaultSeason;

    const dayId = id || dayIdTool.getDayIdFromDate(date);
    const seasonsBeforeDayId = seasons?.filter((season) => season.startDate <= dayId) || [];

    if (seasonsBeforeDayId.length) {
      return seasonsBeforeDayId[seasonsBeforeDayId.length - 1]?.type || defaultSeason;
    }

    return defaultSeason;
  }, [seasons]);

  const getCell = ({
    productId, inventory, dayId, seasonsPrices,
  }) => (
    inventory?.id ? (
      <div className={cn([styles.cell, 'd-flex d-col mobile-a-end'])}>
        <p className="fw-500 m-b-2 fs-16">
          {displayPrice(seasonsPrices[getSeasonForDay({ id: dayId })])}
        </p>

        <TableInputNumber
          withMathSymbols
          value={+inventory.yielding}
          onChange={async (value) => {
            await handleChangeYield(
              inventory.id,
              value,
              productId,
              dayId,
              inventory.yielding,
            );
          }}
          isDisabled={putYieldIsLoading}
          max={10000}
          min={-10000}
          withLeftTransition
          step={50}
        />

        <p className="fw-400 c-neutral-03 m-t-2 opacity-05">
          {`${inventory?.soldQuantity} / `}
          <span className="fw-500 c-neutral-02">{inventory?.quantity}</span>
        </p>
      </div>
    ) : null
  );

  const getCellParams = (product, dayIndex) => ({
    productId: product.id,
    inventory: (
      inventories?.[product.id]?.[dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, dayIndex))]
    ),
    dayId: dayIdTool.getDayIdFromDate(addDays(activeWeekMonday || defaultWeekMonday, dayIndex)),
    seasonsPrices: {
      LOW: +(product.lowSeasonPrice || 0),
      MEDIUM: +(product.mediumSeasonPrice || 0),
      HIGH: +(product.highSeasonPrice || 0),
    },
  });

  const getProductRow = (product) => ({
    id: product.id,
    name: (
      <span data-search={product.name}>
        {product.name}
      </span>
    ),
    day_0: getCell(getCellParams(product, 0)),
    day_1: getCell(getCellParams(product, 1)),
    day_2: getCell(getCellParams(product, 2)),
    day_3: getCell(getCellParams(product, 3)),
    day_4: getCell(getCellParams(product, 4)),
    day_5: getCell(getCellParams(product, 5)),
    day_6: getCell(getCellParams(product, 6)),
  });

  const getColumns = useCallback(() => {
    const daysOfWeek = Array.from(Array(7).keys()).map((_, index) => {
      const date = addDays(activeWeekMonday || defaultWeekMonday, index);

      return {
        key: `day_${index}`,
        title: (
          <div
            className={cn([
              'd-flex d-col fs-14 ws-nowrap',
              styles.season,
              styles[getSeasonForDay({ date })],
            ])}
          >
            <span className={cn([styles.day, 'fs-12 m-b-2'])}>
              {format(date, 'E', { locale: frLocale })}
            </span>
            {format(date, 'dd/MM/yyyy')}
          </div>
        ),
        dataColumn: format(date, 'E dd/MM/yyyy', { locale: frLocale }),
        dataSeason: getSeasonForDay({ date }),
        width: 140,
        isSortable: false,
        dayId: dayIdTool.getDayIdFromDate(date),
      };
    });

    return concat([{
      key: 'name',
      title: (
        <div className="d-flex a-center j-space-btw w-100">
          <span className="m-r-20">{t(`logistic.table.columns.${type}`)}</span>

          <div className={styles.head_legend_wrapper}>
            <span className={cn([styles.head_legend, 'd-flex center'])}>
              <InfoIcon />
            </span>

            <div className={styles.head_legend_details}>
              <div className={styles.head_legend_details_inner}>
                <p>
                  <span className={cn([styles.tag, styles.low])} />
                  {t('seasons.low')}
                </p>
                <p>
                  <span className={cn([styles.tag, styles.medium])} />
                  {t('seasons.medium')}
                </p>
                <p>
                  <span className={cn([styles.tag, styles.high])} />
                  {t('seasons.high')}
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      dataColumn: t(`logistic.table.columns.${type}`),
      isSortable: false,
      bold: true,
      sticky: true,
      width: 200,
    }], daysOfWeek);
  }, [activeWeekMonday, seasons]);

  const handleGoToPrevWeek = () => {
    const dest = subWeeks(activeWeekMonday || defaultWeekMonday, 1);
    if (isAfter(dest, minWeekLimit) || isEqual(dest, minWeekLimit)) {
      setActiveWeekMonday(dest);
    }
  };

  const handleGoToNextWeek = () => {
    const dest = addWeeks(activeWeekMonday || defaultWeekMonday, 1);
    if (isBefore(dest, maxWeekLimit) || isEqual(dest, maxWeekLimit)) {
      setActiveWeekMonday(dest);
    }
  };

  const handleMultipleEdition = () => {
    displayModal({
      maxWidth: 455,
      title: t('batch.title.yields'),
      content: (
        <BatchEdition
          products={
            products?.length
              ? sortBy(products.filter((product) => !isEmpty(inventories?.[product.id])), ['name'])
              : []
          }
          productType={type}
          inventoryType={inventoryType[type]}
          putYieldMutateAsync={putYieldMutateAsync}
          isYielding
        />
      ),
    });
  };

  return (
    <div>
      {products?.length ? (
        <InventoryTable
          isSearchable
          searchPlaceholder={t(`logistic.search.${type}`)}
          rows={
            sortBy(products
              .filter((product) => !isEmpty(inventories?.[product.id])), ['name'])
              .map((product) => getProductRow(product))
          }
          columns={getColumns()}
          filters={(
            <div className="input_max">
              <SelectField
                id="yieldsActiveWeek"
                name="activeWeek"
                options={weeksOptions}
                label={t('logistic.week')}
                value={weeksOptions?.find((opt) => isEqual(opt.value, activeWeekMonday)) || null}
                onChange={(value) => setActiveWeekMonday(value?.value || null)}
              />
            </div>
          )}
          isLoading={isFetching}
          inventories={inventories}
          // isColumnActionLoading={putStatusIsLoading}
          onNavLeft={handleGoToPrevWeek}
          onNavRight={handleGoToNextWeek}
          onMultipleEditionClick={handleMultipleEdition}
          multipleEditionLabel={t('batch.title.yields')}
          isDisabledNavLeft={isBefore(subWeeks(activeWeekMonday || defaultWeekMonday, 1), minWeekLimit)}
          isDisableNavRight={isAfter(addWeeks(activeWeekMonday || defaultWeekMonday, 1), maxWeekLimit)}
          tableClassName="yields_table"
          isYieldsTable
        />
      ) : (
        <EmptyList
          imageTheme="products"
          title={t('yields.empty.title')}
          text={t('yields.empty.text')}
        />
      )}
    </div>
  );
}

Yields.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
  })),
  type: PropTypes.oneOf(['camps', 'extras', 'accommodations']).isRequired,
};

Yields.defaultProps = {
  products: [],
};

export default Yields;
